import { Platform, Appearance } from 'react-native';
import { createReducer, createAction } from '@reduxjs/toolkit';
import type { AnyAction } from '@reduxjs/toolkit';
import DeviceInfo from 'react-native-device-info';
import i18n from 'i18n-js';
import moment from 'moment';

import { backendCall, createApiCall, eventCall, unlogin } from '@reducers/utils';
import { sendLog } from '@reducers/dataDogs';
import { LOGGER_TITLES } from '@reducers/dataDogs/constants';
import { getSecureValue, Alert, isWeb, VERSION, navigateTo, setSecureValue, ROLES } from '@helpers';
import { getBrowser } from '@reducers/imageHandling';
import { initRole, role as frameRole } from '@components/frame';
import { generateTokenInfo } from '@reducers/user';
import { receivedNewAsyncAccount } from '@reducers/user';
import { OPERATOR_RESTRICTIONS } from '@config/operatorRestrictions';
import { trackEvent, TRACKING_EVENTS } from '@services/tracking';
import type { AppThunk } from '@store/types';
import type { ApiError, AppDispatch } from '@reducers/utils/types';
import type {
  DeviceState,
  DeviceItem,
  AuthInfo,
  DeviceActivationInfo,
  SearchDevicesResponse,
  AddDeviceResponse,
  DeleteDeviceResponse,
  SubmitDeviceDetailsResponse,
  AuthenticateDeviceResponse,
  CheckPendingActivationsResponse,
  SearchDevicesRequestDto,
  SubmitDeviceActivationDetailsRequestDto,
  AuthenticateDeviceRequestDto,
  DeviceDetails,
} from './types';

// Re-export types for consumers
export type {
  DeviceState,
  DeviceItem,
  AuthInfo,
  SearchDevicesResponse,
  AddDeviceResponse,
  DeleteDeviceResponse,
  AuthenticateDeviceResponse,
} from './types';

// === Initial State ===

const initState: DeviceState = {
  authInfo: {},
  devices: [],
  loaded: false,
  isDeviceMore: true,
  pageNumber: 1,
  pageSize: 20,
  showDeviceActivation: false,
  authenticationCode: '',
  expirationDateTime: '',
  deviceType: '',
};

// === Actions ===

export const setAuthInfo = createAction<AuthInfo>('device/setAuthInfo');
export const setDevices = createAction<{ devices: DeviceItem[]; isDeviceMore?: boolean }>('device/setDevices');
export const updatePageNumber = createAction('device/updatePageNumber');
export const resetPageNumber = createAction('device/resetPageNumber');
export const openDeviceActivation = createAction<DeviceActivationInfo>('device/openDeviceActivation');
export const closeDeviceActivation = createAction('device/closeDeviceActivation');
export const clearAuthInfo = createAction('device/clearAuthInfo');
export const clear = createAction('clear');

// === Thunks ===

interface GetDevicesProps {
  reset?: boolean;
  orderBy?: SearchDevicesRequestDto['orderBy'];
  size?: number;
  filters?: SearchDevicesRequestDto['filters'];
}

export const getDevices = (props: GetDevicesProps = {}): AppThunk<Promise<void>> => async (dispatch, getState) => {
  const { reset, orderBy, size, filters: incomingFilters } = props;

  if (reset) {
    dispatch(resetPageNumber());
  } else {
    dispatch(updatePageNumber());
  }

  const {
    device: { pageNumber, pageSize, devices },
    user: { userInfo },
  } = getState();

  const operatorEmail = userInfo?.emailAddress;
  const operatorEmailLower = operatorEmail?.toLowerCase();
  const currentRole = frameRole || userInfo?.role;
  const restrictToOperator = currentRole === ROLES.OPERATOR && OPERATOR_RESTRICTIONS.devicesOwnOnly && Boolean(operatorEmailLower);

  // Keep the request simple: ask backend to filter by email (two likely keys)
  const filters = restrictToOperator
    ? {
        ...incomingFilters,
        lastUsedBy: incomingFilters?.lastUsedBy || { value: operatorEmail, valueType: 'string' },
        lastUsedByEmailAddress: incomingFilters?.lastUsedByEmailAddress || { value: operatorEmail, valueType: 'string' },
      }
    : incomingFilters;

  const amount = size || pageSize;

  await dispatch(
    backendCall(
      createApiCall((api) => api.device.getDevices, {
        params: {
          pageNumber,
          pageSize: amount,
          orderBy,
          filters,
        } as SearchDevicesRequestDto,
        onSuccess: (data: SearchDevicesResponse) => {
          const filteredDevices =
            restrictToOperator && operatorEmailLower
              ? (data.devices || []).filter((d) => {
                  const matchesEmail = (value: unknown) => {
                    const str = String(value || '').toLowerCase();
                    if (!str) {
                      return false;
                    }
                    return str === operatorEmailLower || str.includes(operatorEmailLower);
                  };
                  return [
                    d.lastUsedBy,
                    (d as Record<string, unknown>).lastUsedByEmailAddress,
                    (d as Record<string, unknown>).lastUsedByEmail,
                    (d as Record<string, unknown>).registeredByEmailAddress,
                    d.registeredBy,
                  ].some(matchesEmail);
                })
              : data.devices || [];
          const isDeviceMore = (data.devices || []).length === amount;
          if (reset) {
            dispatch(setDevices({ isDeviceMore, devices: filteredDevices }));
          } else {
            dispatch(setDevices({ isDeviceMore, devices: [...devices, ...filteredDevices] }));
          }
        },
        onError: (error) => {
          console.log(error, 'error');
        },
      })
    )
  );
};

export const getDeviceByID = (id: string): AppThunk<Promise<SearchDevicesResponse | undefined>> => async (dispatch) => {
  return await dispatch(
    backendCall(
      createApiCall((api) => api.device.getDevicesByID, {
        params: id,
      })
    )
  );
};

export const getDeviceByOwner = (email: string): AppThunk<Promise<SearchDevicesResponse | undefined>> => async (dispatch) => {
  return await dispatch(
    backendCall(
      createApiCall((api) => api.device.getDevices, {
        params: {
          pageNumber: 1,
          pageSize: 100,
          filters: {
            registeredByEmailAddress: {
              value: email,
              valueType: 'string',
            },
          },
        } as SearchDevicesRequestDto,
      })
    )
  );
};

export const getAuthInfo = (): AppThunk<Promise<void>> => async (dispatch) => {
  dispatch(clearAuthInfo());
  await dispatch(
    backendCall(
      createApiCall((api) => api.device.getAuthInfo, {
        onSuccess: (data: AddDeviceResponse) => {
          const env = getSecureValue('env');
          dispatch(setAuthInfo({ ...data.activationDetails, env } as AuthInfo));
        },
        onError: (error) => {
          console.log(error, 'error');
        },
      })
    )
  );
};

export const submitActivationDetails = (params: SubmitDeviceActivationDetailsRequestDto): AppThunk<Promise<void>> => async (dispatch) => {
  await dispatch(
    backendCall(
      createApiCall((api) => api.device.submitActivationDetails, {
        params,
        onSuccess: (data) => {
          console.log(data, 'responce');
        },
        onError: (error) => {
          console.log(error, 'error');
        },
      })
    )
  );
};

export const authenticateDevice = (data: AuthenticateDeviceRequestDto, onResponse?: (res: unknown) => void): AppThunk<Promise<void>> => async (dispatch) => {
  const call = createApiCall((api) => api.device.authenticateDevice, {
    params: data,
    anonym: true,
  });

  const resp = await dispatch(
    eventCall({
      connectionName: 'signalRAnonym',
      eventName: 'OnAuthenticatedDevice',
      data: call,
      timeout: 60,
      backendCallHandler: onResponse,
    })
  );

  await dispatch(onAuthenticatedDevice(resp));
};

export const authenticateWalboardDevice = (data?: { authenticationCode: string; deviceType: string }): AppThunk<Promise<AuthenticateDeviceResponse | undefined>> => async (dispatch, getState) => {
  const { signalR } = getState();
  const obj = data ? data : signalR;
  const { authenticationCode, deviceType } = obj;
  return await dispatch(
    backendCall(
      createApiCall((api) => api.device.authenticateWalboardDevice, {
        params: { authenticationCode, deviceType } as AuthenticateDeviceRequestDto,
        onSuccess: (data: AuthenticateDeviceResponse) => {
          Alert({ title: i18n.t(data.isSuccess ? 'Success' : 'Error'), type: data.isSuccess ? 'success' : 'danger' });
        },
        onError: (error) => {
          console.log(error, 'error');
        },
      })
    )
  );
};

export const getDeviceDetails = async (): Promise<DeviceDetails> => {
  const getUniqueId = getSecureValue('getUniqueId');
  let browser = '';

  if (isWeb) {
    browser = getBrowser();
  }

  return {
    deviceId: isWeb ? getUniqueId : await DeviceInfo.getUniqueId(),
    name: isWeb ? browser : await DeviceInfo.getModel(),
    theme: Appearance.getColorScheme(),
    language: i18n.currentLocale() || 'en',
    make: isWeb ? browser : DeviceInfo.getBrand(),
    model: isWeb ? browser : await DeviceInfo.getModel(),
    platform: Platform.OS,
    appVersion: VERSION,
  };
};

export const onRequestForDeviceDetails = (e: { deviceId: string }): AppThunk<Promise<void>> => async (dispatch) => {
  let deviceId;

  if (isWeb) {
    deviceId = getSecureValue('getUniqueId');
  } else {
    deviceId = await DeviceInfo.getUniqueId();
  }

  if (e.deviceId === deviceId) {
    dispatch(updateDevice());
  }
};

export const updateDevice = (notificationToken?: string): AppThunk<Promise<SubmitDeviceDetailsResponse | undefined>> => async (dispatch) => {
  const token = getSecureValue('token');
  if (!token) {
    return undefined;
  }
  const data = await getDeviceDetails();

  // Cache new push token or retrieve cached one so every updateDevice call includes it
  if (notificationToken) {
    setSecureValue('pushToken', notificationToken);
  }
  const cachedPushToken = notificationToken || getSecureValue('pushToken');

  dispatch(sendLog({
    title: LOGGER_TITLES.UPDATE_DEVICE_SUBMIT,
    data: {
      hasAuthToken: !!token,
      pushTokenSource: notificationToken ? 'fresh' : (cachedPushToken ? 'cached' : 'none'),
      deviceData: data,
      platform: Platform.OS,
    },
    status: 'debug',
  }) as unknown as AnyAction);

  if (cachedPushToken) {
    data.pushApiRegistrationToken = cachedPushToken;
  }

  return await dispatch(
    backendCall(
      createApiCall((api) => api.device.updateDevice, {
        params: { device: data },
        onError: (error) => {
          if (error && error.code === 404) {
            Alert({
              title: 'Your device was removed',
              actions: [
                {
                  text: i18n.t('OK'),
                  onPress: () => {},
                  style: 'primary',
                },
              ],
            });
            dispatch(unlogin());
          }
        },
      })
    )
  );
};

export const updateDeviceByID = (data: DeviceDetails, callBack?: () => void): AppThunk<Promise<SubmitDeviceDetailsResponse | undefined>> => async (dispatch) => {
  return await dispatch(
    backendCall(
      createApiCall((api) => api.device.updateDevice, {
        params: { device: data },
        onSuccess: () => {
          if (callBack) {
            callBack();
          }
        },
      })
    )
  );
};

export const deleteDevice = (id: string): AppThunk<Promise<DeleteDeviceResponse | undefined>> => async (dispatch, getState) => {
  const {
    device: { devices, loaded },
  } = getState();

  return await dispatch(
    backendCall(
      createApiCall((api) => api.device.deleteDevice, {
        onSuccess: (data: DeleteDeviceResponse) => {
          if (data.isSuccess && devices && devices.length && loaded) {
            dispatch(setDevices({ devices: devices.filter((d) => d.deviceId !== id) }));
          }
        },
        params: { id },
      })
    )
  );
};

export const checkPendingActivations = (): AppThunk<Promise<CheckPendingActivationsResponse | undefined>> => async (dispatch) => {
  return await dispatch(
    backendCall(
      createApiCall((api) => api.device.checkPendingActivations, {})
    )
  );
};

export const onAddedDevice = (e: unknown): AppThunk<Promise<void>> => async (dispatch) => {
  if (e) {
    dispatch(getAuthInfo());
    Alert({ title: 'Success' });
    trackEvent(TRACKING_EVENTS.DEVICE_MANAGEMENT, {
      action: 'added',
      deviceType: (e as Record<string, unknown>)?.deviceType as string || '',
    });
  }
};

export const onLogOffDevice = (e: { deviceId: string }): AppThunk<Promise<void>> => async (dispatch) => {
  if (!isWeb) {
    const deviceId = await DeviceInfo.getUniqueId();
    if (e.deviceId === deviceId) {
      dispatch(unlogin());
    }
  }
};

export const onRequestForNewDeviceConfirmation = (e: { authenticationCode?: string; deviceType: string; expirationDateTime: string }): AppThunk<Promise<void>> => async (dispatch) => {
  if (!e.authenticationCode) {
    return;
  }
  const { deviceType, authenticationCode, expirationDateTime } = e;
  const exDate = moment.utc(expirationDateTime).diff(moment(), 'seconds');
  if (exDate <= 1) {
    return;
  }
  dispatch(openDeviceActivation({ deviceType, authenticationCode, expirationDateTime }));
};

export const onAuthenticatedDevice = (e: Record<string, unknown>): AppThunk<Promise<void>> => async (dispatch) => {
  if (!e.isSuccess) {
    return;
  }

  setSecureValue('token', e.accessToken as string);
  setSecureValue('refreshToken', e.refreshToken as string);
  setSecureValue('expiresIn', moment().add(e.expiresIn as number, 's').format('YYYY-MM-DD HH:mm:ss'));
  await generateTokenInfo(e.accessToken as string, e.deviceCode as string);

  await dispatch(updateDevice());

  const waitForNewAccount = await dispatch(receivedNewAsyncAccount());
  if (waitForNewAccount) {
    return;
  }

  await initRole();
  navigateTo('Authorized');
};

// === Reducer ===

export default createReducer<DeviceState>(initState, (builder) => {
  builder
    .addCase(setAuthInfo, (state, action) => ({
      ...state,
      authInfo: action.payload,
    }))
    .addCase(updatePageNumber, (state) => ({
      ...state,
      pageNumber: state.pageNumber + 1,
    }))
    .addCase(resetPageNumber, (state) => ({
      ...state,
      pageNumber: 1,
    }))
    .addCase(setDevices, (state, action) => ({
      ...state,
      loaded: true,
      ...action.payload,
    }))
    .addCase(openDeviceActivation, (state, action) => ({
      ...state,
      showDeviceActivation: true,
      authenticationCode: action.payload.authenticationCode,
      expirationDateTime: action.payload.expirationDateTime,
      deviceType: action.payload.deviceType,
    }))
    .addCase(closeDeviceActivation, (state) => ({
      ...state,
      showDeviceActivation: false,
      authenticationCode: '',
      expirationDateTime: '',
    }))
    .addCase(clearAuthInfo, (state) => ({
      ...state,
      authInfo: {},
    }))
    .addCase(clear, () => {
      return initState;
    });
});
